import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import {
  deletePlacement,
  getAccommodationStatus,
  getEmailSettingsForSite,
  setIsConfirmDeleteModalOpen,
  getPlacementDetails,
  setPlacementDetails,
  setPlacementId,
  getPlacementSite,
  setIsConfirmUpdateRestrictionsModalOpen,
  setIsAlternativePlacementModalOpen,
  setIsRestrictionsModalOpen,
  setIsConfirmTransferModalOpen,
  setIsUpdateRestrictionDatesModalOpen,
  getPlacementIdByExternalIdAndRedirect,
  setRedirectUrl,
  getUpdateRestrictionAllowed,
  setUploadRfiModalOpen,
  initiatePlacementTransfer,
  getAccommodationStatusForAccommodation,
  setIsRestrictionEntriesErrorModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { setSelectedTab } from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import {
  setJmrFlowType,
  clearProcessingMessagesData,
  getRegionTasksById,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { getAllowedSites } from 'src/store/siteSlice';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import { createToast } from 'src/store/toastsSlice';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import Breadcrumbs from 'src/components/shared/breadcrumbs/breadcrumbs';
import { constructIncidentLink, constructMedicalAttachmentsLink } from 'src/utils/constructRTWLinks';
import { stripOutLeadingHash } from 'src/utils/helpers';
import {
  PreSelectedTask,
  setupPreselectedRestrictionListItems,
  validateIncomingPreSelectedRestrictionItems,
} from 'src/utils/restrictionUtils';
import { useAppSelector, AppDispatch } from 'src/store/store';
import PlacementSelections from './subpages/placementSelections/placementSelections';
import Restrictions from './subpages/restrictions/restrictions';
import EmployeeInformation from './subpages/employeeInformation/employeeInformation';
import RevisionHistory from './subpages/revisionHistory/revisionHistory';
import Correspondence from './subpages/correspondence/correspondence';
import TwpaModal from './twpaModal/twpaModal';
import OomdModal from './oomdModal/oomdModal';
import SendEmailModal from './sendEmailModal/sendEmailModal';
import TopOptions from './topOptions/topOptions';
import TopDetails from './topDetails/topDetails';
import TopTabs from './topTabs/topTabs';
import InitiateTransferModal from './initiateTransferModal/initiateTransferModal';
import ConfirmUpdateRestrictionsModal from './confirmUpdateRestrictionsModal/confirmUpdateRestrictionsModal';
import './placementDetails.scss';
import { CaseSources, JmrFlowTypes } from 'src/utils/jmrUtils';
import UpdatePlacementStatusModal from './updatePlacementStatusModal/updatePlacementStatusModal';
import { PlacementDetails } from 'src/store/placementDetailsPageSlice/types';

/**
 * Default component.
 */
const PlacementDetailsPg = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { placementId, type, caseId, login } = useParams();
  const { userLoading, featureFlags } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const { accommodationType, processingMessages, externalCaseId, consolidatedRegionTasks } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const {
    selectedTab,
    isFetchingPlacementDetails,
    placementDetails,
    isConfirmDeleteModalOpen,
    isConfirmUpdateRestrictionsModalOpen,
    isAlternativePlacementModalOpen,
    isUpdateRestrictionDatesModalOpen,
    isConfirmTransferModalOpen,
    redirectUrl,
    isUploadRfiModalOpen,
    isSiteParticipatingInDecisionHubPilot,
    isRestrictionEntriesErrorModalOpen,
    placementSite,
  } = useAppSelector((state) => state.placementDetailsPage);
  const areEmailSettingsFetched = useRef(false);
  const arePlacementDetailsFetched = useRef(false);
  const areRegionTasksFetched = useRef(false);
  const [isTwpaModalOpen, setIsTwpaModalOpen] = useState(false);

  // Initalize "dispatchToast()"" function
  const dispatchToast = (type: 'error' | 'success', message: string, incidentLink?: string | null) =>
    dispatch(
      createToast({
        type: type,
        message: (
          <>
            <div>{message}</div>
            {incidentLink && (
              <div>
                <Link target="_blank" rel="noopener noreferrer" type="secondary" href={incidentLink}>
                  {incidentLink}
                </Link>
              </div>
            )}
          </>
        ),
        timeout: TOAST_TIMEOUT,
      }),
    );

  // Show toast when messages are done processing
  useEffect(() => {
    if (processingMessages && accommodationType === 'AUSTIN') {
      const incidentLink = externalCaseId && constructIncidentLink(stripOutLeadingHash(externalCaseId));
      processingMessages.forEach((message) => {
        if (message.type === 'ERROR') {
          if (message.content.includes('AUSTIN')) {
            dispatchToast('error', message.content, incidentLink);
          } else {
            dispatchToast('error', message.content);
          }
        } else if (message.type === 'SUCCESS') {
          dispatchToast('success', message.content);
        }
      });
      dispatch(clearProcessingMessagesData());
    }
  }, [processingMessages]);

  // Delete a placement
  const removePlacement = () => {
    if (placementId) {
      dispatch(deletePlacement(+placementId));
      dispatch(setIsConfirmDeleteModalOpen(false));
      navigate('/placementsOverview');
    }
  };

  // Navigate to update JMR flow
  const navigateToUpdateJmr = (updateType: JmrFlowTypes) => {
    dispatch(setIsRestrictionsModalOpen(false));
    dispatch(setIsAlternativePlacementModalOpen(false));
    dispatch(setJmrFlowType(updateType));
    dispatch(setIsRestrictionEntriesErrorModalOpen(false));
    navigate(`/newJMR/${placementId}/update/${updateType}`);
  };

  // Navigate to Confirm update restrictions from the modal
  const confirmUpdateRestrictions = async () => {
    if (placementId && !doesPlacementHaveRestrictionErrors) {
      const id = parseInt(placementId);
      const isUpdateRestrictionAllowed = await dispatch(getUpdateRestrictionAllowed(id)).unwrap();
      if (isUpdateRestrictionAllowed) {
        navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
      } else {
        dispatch(setIsConfirmUpdateRestrictionsModalOpen(false));
        dispatch(setUploadRfiModalOpen(true));
      }
    } else {
      navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
    }
  };

  // When the page unloads
  useEffect(() => {
    return () => {
      dispatch(setPlacementDetails({} as PlacementDetails));
      // Reset the tab to placement-selections when leaving this component so that the
      // next time you enter this component, it defaults to placement-selections
      dispatch(setSelectedTab('placement-selections'));
    };
  }, []);

  // Handle placement details
  useEffect(() => {
    if (userLoading === 'fulfilled') {
      dispatch(getAllowedSites());
      if (type && caseId && login) {
        // Note that this will set the "redirectUrl" in redux
        dispatch(getPlacementIdByExternalIdAndRedirect({ type: type, caseId: caseId, login: login }));
      } else if (placementId) {
        if (!arePlacementDetailsFetched.current) {
          arePlacementDetailsFetched.current = true;
          dispatch(getPlacementDetails(+placementId));
        }
        dispatch(setPlacementId(+placementId));
      }
    }
  }, [userLoading]);

  // Get email settings
  useEffect(() => {
    if (placementDetails.fc && allowedSites.length > 0 && !areEmailSettingsFetched.current) {
      dispatch(getEmailSettingsForSite());
      areEmailSettingsFetched.current = true;
    }
  }, [placementDetails.fc, allowedSites]);

  // Fetch the site details
  useEffect(() => {
    if (placementDetails.siteId) {
      dispatch(getPlacementSite(placementDetails.siteId));
    }
  }, [placementDetails.siteId]);

  // If the site changes
  useEffect(() => {
    if (isSiteParticipatingInDecisionHubPilot !== undefined && placementId !== undefined) {
      const shouldShowStaticStatuses = !featureFlags.DECISION_PORTAL || !isSiteParticipatingInDecisionHubPilot;
      if (shouldShowStaticStatuses) {
        if (placementDetails.siteId) {
          dispatch(getAccommodationStatus(placementDetails.siteId));
        }
      } else {
        dispatch(getAccommodationStatusForAccommodation(+placementId));
      }
    }
  }, [placementDetails.siteId, placementDetails.accommodationStatus, isSiteParticipatingInDecisionHubPilot]);

  // Once we can get the regionId, fetch the the regionTasks. We need the regionTasks to
  // get the "consolidatedRegionTasks" which is used to determine if there are any
  // restrictionEntries that have errrors
  useEffect(() => {
    if (!areRegionTasksFetched.current && placementDetails.siteId && allowedSites.length > 0) {
      const allowedSite = allowedSites.find((site) => site.id === placementDetails.siteId);
      const regionId = allowedSite?.regionId ?? -1;
      if (regionId > -1) {
        areRegionTasksFetched.current = true;
        dispatch(getRegionTasksById(regionId));
      }
    }
  }, [allowedSites, placementDetails.siteId]);

  // Determine if there are any errors in the Placement's restrictions
  const doesPlacementHaveRestrictionErrors = useMemo(() => {
    if (consolidatedRegionTasks.length > 0 && placementDetails.workRestrictions?.length > 0) {
      // Setup the selected tasks
      const selectedTasks = placementDetails.workRestrictions.map((restriction) => ({
        taskId: restriction.taskId,
        label: restriction.taskName,
        restrictedValue: restriction.restrictedValue,
        measureUnit: restriction.measureUnit,
        bodyLocationValue: restriction.bodyLocation,
      })) as PreSelectedTask[];
      // Determine if there are validation errors
      const preSelectedRestrictionItems = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
      const shiftLength = placementDetails.modifiedShiftLength;
      const isValid = validateIncomingPreSelectedRestrictionItems(preSelectedRestrictionItems, shiftLength);
      return !isValid;
    } else {
      return false;
    }
  }, [consolidatedRegionTasks, placementDetails.workRestrictions]);

  // Show toasts if redirect URL changes
  useEffect(() => {
    if (redirectUrl !== '') {
      dispatch(setRedirectUrl(''));
      if (redirectUrl.startsWith('/placementDetails')) {
        dispatch(
          createToast({
            type: 'success',
            message: 'Existing RTW placement found, redirecting to the Placement Details page...',
            timeout: TOAST_TIMEOUT,
          }),
        );
        navigate(redirectUrl, { replace: true });
        const placementId = Number(redirectUrl.split('/').pop());
        dispatch(getPlacementDetails(placementId));
        dispatch(setPlacementId(placementId));
      } else if (redirectUrl.startsWith('/placementsOverview')) {
        navigate(`${redirectUrl}`);
      } else {
        dispatch(
          createToast({
            type: 'success',
            message: 'An RTW placement cannot be found for the selected AUSTIN Case. Please create one below...',
            timeout: TOAST_TIMEOUT,
          }),
        );
        navigate(`${redirectUrl}/${CaseSources.Austin}/${login}/${caseId}`);
      }
    }
  }, [redirectUrl]);

  return (
    <div className="placement-details">
      <LoadingSpinnerWrapper showSpinner={isFetchingPlacementDetails}>
        {placementId && (
          <>
            {/* Breadcrumbs */}
            <Breadcrumbs
              breadcrumbList={[
                {
                  label: `Placements Overview ${placementDetails.fc}`,
                  url: '/placementsOverview',
                },
                {
                  label: `Placement Details (${placementDetails.associateFullName || placementDetails.login})`,
                  url: `${PLACEMENT_DETAILS_BASEURL}/${placementId}`,
                },
              ]}
            />

            {/* Main Body */}
            <div className="placement-details__content">
              <TopOptions
                doesPlacementHaveRestrictionErrors={doesPlacementHaveRestrictionErrors}
                openTwpaModal={() => setIsTwpaModalOpen(true)}
              />
              <TopDetails />
              <TopTabs />
              <UpdatePlacementStatusModal />
              {placementDetails?.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID ? (
                <OomdModal />
              ) : (
                <TwpaModal
                  placementId={Number(placementId)}
                  isTwpaModalOpen={isTwpaModalOpen}
                  closeTwpaModal={() => setIsTwpaModalOpen(false)}
                  allocatedJobShifts={placementDetails?.allocatedJobShifts}
                  isJobModificationsEnabledForSite={placementSite?.jobModificationsEnabled ?? false}
                />
              )}
              <SendEmailModal />
              <Row spacingInset="400 none 600 none" widths={['fill']}>
                {selectedTab === 'placement-selections' && <PlacementSelections />}
                {selectedTab === 'restrictions' && (
                  <Restrictions doesPlacementHaveRestrictionErrors={doesPlacementHaveRestrictionErrors} />
                )}
                {selectedTab === 'employee-information' && (
                  <EmployeeInformation isSiteParticipatingInDecisionHubPilot={isSiteParticipatingInDecisionHubPilot} />
                )}
                {selectedTab === 'revision-history' && <RevisionHistory />}
                {selectedTab === 'correspondence' && <Correspondence />}
              </Row>
            </div>
          </>
        )}
      </LoadingSpinnerWrapper>

      {/* Modals */}
      <ConfirmModal
        modalTitle="Confirm delete?"
        modalDescription="This will permanently delete this placement"
        open={isConfirmDeleteModalOpen}
        setOpen={(open) => dispatch(setIsConfirmDeleteModalOpen(open))}
        onConfirm={() => removePlacement()}
      />
      <ConfirmModal
        modalTitle="Update Placement Restrictions?"
        modalDescription="If restrictions and start/end dates have changed, Restrictions will navigate you to a page where 
          you will enter new restrictions and dates."
        open={isConfirmUpdateRestrictionsModalOpen}
        setOpen={(open) => dispatch(setIsConfirmUpdateRestrictionsModalOpen(open))}
        onConfirm={confirmUpdateRestrictions}
      />
      <ConfirmModal
        modalTitle="Upload RFI or WSR?"
        modalDescription="You must upload a new RFI or WSR to update the restrictions for this placement. If a new RFI or WSR has 
          already been uploaded, please wait a few minutes and try again."
        open={isUploadRfiModalOpen}
        setOpen={(open) => dispatch(setUploadRfiModalOpen(open))}
        onConfirm={() => dispatch(setUploadRfiModalOpen(false))}
        includeLink={true}
        linkText="Austin Incident Link"
        linkUrl={placementDetails.externalCaseId && constructMedicalAttachmentsLink(placementDetails.externalCaseId)}
        showCancelButton={false}
      />
      <ConfirmModal
        modalTitle="Select Alternative Placements?"
        modalDescription="If the site decision maker accepted an alternative placement, Placement Selections(s) will navigate you 
          to a page where you will select new placements."
        open={isAlternativePlacementModalOpen}
        setOpen={(open) => dispatch(setIsAlternativePlacementModalOpen(open))}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.AlternativePlacementRecommended)}
      />
      <ConfirmModal
        modalTitle="Update Restriction Dates?"
        modalDescription="If ONLY start/end dates have changed (restrictions have remained the same), Restriction Dates will navigate 
          you to a page where you will select new start and end dates."
        open={isUpdateRestrictionDatesModalOpen}
        setOpen={(open) => dispatch(setIsUpdateRestrictionDatesModalOpen(open))}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.UpdateDates)}
      />
      <InitiateTransferModal />
      <ConfirmModal
        modalTitle="Confirm Transfer?"
        modalDescription="Once transferred, your site will no longer have access to this RTW placement."
        open={isConfirmTransferModalOpen}
        setOpen={(open) => dispatch(setIsConfirmTransferModalOpen(open))}
        onConfirm={() => {
          dispatch(initiatePlacementTransfer());
          dispatch(setIsConfirmTransferModalOpen(false));
        }}
      />
      <ConfirmUpdateRestrictionsModal
        open={isRestrictionEntriesErrorModalOpen}
        onClose={() => dispatch(setIsRestrictionEntriesErrorModalOpen(false))}
        submit={() => navigateToUpdateJmr(JmrFlowTypes.NewRestrictions)}
      />
    </div>
  );
};

export default PlacementDetailsPg;
